import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";

const PERMISSIONS = {
    createRecurringTask: "recurring-task.create",
    readRecurringTask: "recurring-task.read",
    updateRecurringTask: "recurring-task.update",
    deleteRecurringTask: "recurring-task.delete",

    createDocument: "document.create",
    readDocument: "document.read",
    updateDocument: "document.update",
    deleteDocument: "document.delete",

    createSpace: "space.create",
    readSpace: "space.read",
    updateSpace: "space.update",
    deleteSpace: "space.delete",

    createAsset: "asset.create",
    readAsset: "asset.read",
    updateAsset: "asset.update",
    deleteAsset: "asset.delete",

    createCategory: "category.create",
    readCategory: "category.read",
    updateCategory: "category.update",
    deleteCategory: "category.delete",

    createUser: "user.create",
    readUser: "user.read",
    updateUser: "user.update",
    deleteUser: "user.delete",

    readMessengerFlow: "messenger-flow.read",
    updateMessengerFlow: "messenger-flow.update",

    readBilling: "billing.read",
    updateBilling: "billing.update",

    createRole: "role.create",
    readRole: "role.read",
    updateRole: "role.update",
    deleteRole: "role.delete",

    limitedAccess: "task.limited-access",
    completeTask: "task.complete-task",
    publicReply: "task.public-reply",

    updateTenant: "tenant.update",

    createTime: "task.time.create",
    deleteTime: "task.time.delete",

    createMaterial: "task.material.create",
    deleteMaterial: "task.material.delete",

    createTag: "tag.create",
    deleteTag: "tag.delete",

    createChecklist: "checklist.create",
    updateChecklist: "checklist.update",
    deleteChecklist: "checklist.delete",
};

const PERMISSIONS_ORDER = {
    read: 1,
    update: 2,
    create: 3,
    delete: 4,
};

export default {
    install: (app) => {
        const activeTenant = computed(() => {
            return usePage().props.tenant;
        });

        // Check if the route is on the orginizational level
        // It should include the word "organization" in the route
        // TODO remove this once we don't receive the tenant object in every request
        const isOrgLevel = computed(() => {
            return usePage().url.value?.includes("organisation");
        });

        const userPermissions = computed(() => {
            if (!isOrgLevel.value && activeTenant.value?.id) {
                return usePage().props.auth.permissions[activeTenant.value.id];
            }

            // If no tenant is active, return all permissions across all tenants
            return Object.values(usePage().props.auth.permissions)?.reduce(
                (acc, curr) => {
                    return [...acc, ...curr];
                },
                []
            );
        });

        const hasPermission = (key, tenantId) => {
            if (tenantId) {
                return (
                    usePage().props.auth.permissions[tenantId]?.includes(key) ??
                    false
                );
            }

            return userPermissions.value?.includes(key) ?? false;
        };

        const tenantsWithUserPermission = (key) =>
            Object.entries(usePage().props.auth.permissions).reduce(
                (acc, [tenantId, permissions]) =>
                    permissions?.includes(key) ? [...acc, tenantId] : acc,
                []
            );

        const applyPermissionsOrder = (permissionGroup) => {
            return {
                ...permissionGroup,
                permissions: permissionGroup.permissions.sort((a, b) => {
                    const aKey = a.name.split(".")[1];
                    const bKey = b.name.split(".")[1];

                    return PERMISSIONS_ORDER[aKey] - PERMISSIONS_ORDER[bKey];
                }),
            };
        };

        // Provide the hasPermissions method and permissions enum
        app.provide("permissions", {
            permissions: PERMISSIONS,
            hasPermission,
            tenantsWithUserPermission,
            applyPermissionsOrder,
        });
    },
};
